import ButtonGoBack from '@/components/buttonGoBack';
import DataGrid from '@/components/dataGrid/dataGrid';
import ShowHide from '@/components/showHide';
import Tooltip from '@/components/tooltip';
import { Company } from '@/modules/company/entities/interfaces/company';
import { DialogConfirmDeleteDigitalHospitalConfig } from '@/modules/companyDigitalHospitalConfig/components/dialogConfirmDeleteDigitalHospitalConfig';
import { DialogSaveChangesBeforeLeaveThePage } from '@/modules/companyDigitalHospitalConfig/components/dialogSaveChangesBeforeLeaveThePage';
import { DigitalHospitalSteps } from '@/modules/companyDigitalHospitalConfig/entities/enums/digitalHospital.enums';
import { ListParams } from '@/modules/companyDigitalHospitalConfig/entities/interfaces/digitalHospital.interfaces';
import { useDigitalHospitalRequests } from '@/modules/companyDigitalHospitalConfig/hooks/useDigitalHospitalRequests';
import { useDigitalHospitalSelectedCompanyIds } from '@/modules/companyDigitalHospitalConfig/hooks/useDigitalHospitalSelectedCompanyIds';
import { useSaveChangeBeforeLeaveThePage } from '@/modules/companyDigitalHospitalConfig/hooks/useSaveChangeBeforeLeaveThePage';
import { LIST_INITIAL_PAGE, LIST_INITIAL_PAGE_SIZE } from '@/shared/entities/constants/list';
import { useQueryStringState } from '@/shared/hooks/useQueryStringState';
import ICONS from '@/shared/entities/constants/icons';
import { checkPermission, PERMISSIONS } from '@/utils/checkPermission';
import formatText from '@/utils/formatText';
import React, { useState } from 'react';
import { Button, Checkbox, Chip, Tabs, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

export const List: React.FC = () => {
  const hasPermissionToManageDigitalHospital = checkPermission(PERMISSIONS.COMPANY_DIGITAL_HOSPITAL_MANAGE);

  const [listParams, setListParams] = useQueryStringState<ListParams>({
    stepDigitalHospital: DigitalHospitalSteps.DISABLED,
  });

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);

  const {
    selectedCompanyIdsByStep,
    setSelectedCompanyIdsByStep,
    companyIdsOnPageByStep,
    setCompanyIdsOnPageByStep,
    selectedCompanyIdsOnPage,
    updateCompanyIds,
  } = useDigitalHospitalSelectedCompanyIds({
    stepDigitalHospital: listParams.stepDigitalHospital,
  });

  const { saving, deleting, saveManyCompanyConfigs, deleteManyCompanyConfigs, getPaginatedList } =
    useDigitalHospitalRequests({
      requestCallback: () => {
        setSelectedCompanyIdsByStep((current) => ({
          ...current,
          [listParams.stepDigitalHospital]: [],
        }));
        setListParams((current) => ({
          ...current,
          page: 1,
        }));
      },
    });

  const { openConfirmationDialog, answerConfirmation } = useSaveChangeBeforeLeaveThePage({
    shouldConfirm: selectedCompanyIdsByStep[listParams.stepDigitalHospital].length > 0,
    onConfirmSaveChanges: async () => {
      listParams.stepDigitalHospital === DigitalHospitalSteps.ENABLED
        ? await deleteManyCompanyConfigs(selectedCompanyIdsByStep[DigitalHospitalSteps.ENABLED])
        : await saveManyCompanyConfigs(selectedCompanyIdsByStep[DigitalHospitalSteps.DISABLED]);
    },
  });

  return (
    <>
      <DialogConfirmDeleteDigitalHospitalConfig
        open={openConfirmDeleteDialog}
        setOpen={setOpenConfirmDeleteDialog}
        confirm={async (confirmation) => {
          if (confirmation) {
            await deleteManyCompanyConfigs(selectedCompanyIdsByStep[listParams.stepDigitalHospital]);
          }

          setOpenConfirmDeleteDialog(false);
        }}
      />

      <DialogSaveChangesBeforeLeaveThePage
        open={Boolean(openConfirmationDialog)}
        confirm={(response) => answerConfirmation?.(response)}
      />

      <Grid column gap={16} stretch>
        <Grid alignContent='justify' alignItems='center' stretch>
          <Tabs
            elements={[]}
            actions={[
              {
                label: 'Desabilitadas',
                active: listParams.stepDigitalHospital == DigitalHospitalSteps.DISABLED,
              },
              {
                label: 'Habilitadas',
                active: listParams.stepDigitalHospital == DigitalHospitalSteps.ENABLED,
              },
            ]}
            onChange={(stepNumber) => {
              setListParams({
                stepDigitalHospital:
                  stepNumber === 0 ? DigitalHospitalSteps.DISABLED : DigitalHospitalSteps.ENABLED,
                pageSize: LIST_INITIAL_PAGE_SIZE,
                page: LIST_INITIAL_PAGE,
              });
            }}
          />

          <ButtonGoBack />
        </Grid>

        <ShowHide transition='slideToDown' visible={!hasPermissionToManageDigitalHospital}>
          <Text
            value='Você não tem permissão para gerenciar indicadores de hospital digital.'
            color='warning'
            icon={ICONS.WARNING}
            iconColor='warning'
            applyBaseColor
            rounded
            padding='8px 12px'
            size='medium'
          />
        </ShowHide>

        <DataGrid
          key={listParams.stepDigitalHospital}
          filterStateController={[listParams, setListParams]}
          source={async (filters) => {
            const response = await getPaginatedList(filters);
            setCompanyIdsOnPageByStep((current) => ({
              ...current,
              [listParams.stepDigitalHospital]: response.data.map((company) => company.id),
            }));

            return response;
          }}
          filters={[
            {
              label: 'Buscar grupo empresarial',
              key: 'parentCompanyTerm',
              width: 50,
            },
            {
              label: 'Buscar empresa',
              key: 'q',
              width: 50,
            },
          ]}
          columns={[
            {
              headerName: (
                <Grid alignItems='center'>
                  <Tooltip
                    title={
                      !hasPermissionToManageDigitalHospital
                        ? 'Você não tem permissão para gerenciar indicadores de hospital digital.'
                        : undefined
                    }>
                    <Checkbox
                      small
                      disabled={
                        saving ||
                        deleting ||
                        companyIdsOnPageByStep[listParams.stepDigitalHospital].length === 0 ||
                        !hasPermissionToManageDigitalHospital
                      }
                      indeterminate={
                        selectedCompanyIdsOnPage.length > 0 &&
                        selectedCompanyIdsOnPage.length <
                          companyIdsOnPageByStep[listParams.stepDigitalHospital].length
                      }
                      checked={
                        selectedCompanyIdsOnPage.length ===
                          companyIdsOnPageByStep[listParams.stepDigitalHospital].length &&
                        companyIdsOnPageByStep[listParams.stepDigitalHospital].length > 0
                      }
                      onChange={(checked: boolean) => {
                        updateCompanyIds(
                          checked,
                          listParams.stepDigitalHospital,
                          companyIdsOnPageByStep[listParams.stepDigitalHospital],
                        );
                      }}
                    />
                  </Tooltip>
                </Grid>
              ),
              render: ({ id }: Company) => (
                <Tooltip
                  title={
                    !hasPermissionToManageDigitalHospital
                      ? 'Você não tem permissão para gerenciar indicadores de hospital digital.'
                      : undefined
                  }>
                  <Checkbox
                    small
                    disabled={saving || deleting || !hasPermissionToManageDigitalHospital}
                    checked={selectedCompanyIdsOnPage.includes(id)}
                    onChange={(checked: boolean) => {
                      updateCompanyIds(checked, listParams.stepDigitalHospital, [id]);
                    }}
                  />
                </Tooltip>
              ),
            },
            {
              headerName: 'Grupo empresarial',
              render: ({ parent, name }: Company) => (
                <Text size='medium' value={parent?.name || name} />
              ),
              sub: {
                render: ({ parent, cnpj }: Company) => (
                  <Text value={formatText(parent?.cnpj || cnpj, 'cnpj')} />
                ),
              },
            },
            {
              headerName: 'Empresa',
              field: 'name',
              sub: {
                field: 'cnpj',
                formatData: 'cnpj',
              },
            },
            {
              headerName: 'Indicadores de hospital digital',
              render: () => (
                <Chip
                  rounded
                  size='small'
                  color='light'
                  label={
                    listParams.stepDigitalHospital === DigitalHospitalSteps.ENABLED
                      ? 'Habilitado'
                      : 'Desabilitado'
                  }
                />
              ),
            },
          ]}
        />

        <Grid stretch alignContent='right'>
          <Button
            label={
              listParams.stepDigitalHospital === DigitalHospitalSteps.DISABLED
                ? 'Habilitar indicadores de hospital digital'
                : 'Dasabilitar indicadores de hospital digital'
            }
            disabled={
              selectedCompanyIdsByStep[listParams.stepDigitalHospital].length === 0 || saving || deleting
            }
            loading={saving || deleting}
            onClick={async () => {
              listParams.stepDigitalHospital === DigitalHospitalSteps.ENABLED
                ? setOpenConfirmDeleteDialog(true)
                : await saveManyCompanyConfigs(selectedCompanyIdsByStep[listParams.stepDigitalHospital]);
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
